import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { TeamService } from '../services/teamService';
import { TeamMember, UserRole } from '@/types/team';
import { supabase } from '@/integrations/supabase/client';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';
import { disconnectPresence } from '@/hooks/usePresence';

interface AuthContextType {
  teamMember: TeamMember | null;
  currentRole: UserRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  siteOfficeId: string | null; // null = All Site Office (akses semua)
  canAccessAllSiteOffices: boolean;
  canAccessSiteOffice: (siteOfficeId: string) => boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; needsEmailConfirmation?: boolean }>;
  login: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  checkMembershipStatus: (email: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [teamMember, setTeamMember] = useState<TeamMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const signInHandledRef = React.useRef(false);
  const { refreshSiteOffices, setUserSiteOfficeAccess, clearSiteOffice } = useSiteOffice();

  useEffect(() => {
    // Recover session from Supabase Auth + localStorage
    const recoverSession = async () => {
      try {
        // First check Supabase auth session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Supabase session is valid - recover team member from DB
          const currentMember = await TeamService.findTeamMemberByEmail(session.user.email!.toLowerCase());
          
          if (currentMember && currentMember.status === 'active' && !currentMember.isBlocked) {
                setTeamMember(currentMember);
                localStorage.setItem('currentUser', JSON.stringify(currentMember));
                localStorage.setItem('userRole', currentMember.role || 'viewer');
                localStorage.setItem('userSiteOfficeId', currentMember.siteOfficeId === null || currentMember.siteOfficeId === undefined ? 'null' : currentMember.siteOfficeId);
                window.dispatchEvent(new Event('userRoleChanged'));
                console.log('✅ Session recovered from Supabase Auth for', currentMember.name);
                // Refresh site offices now that we're authenticated
                await refreshSiteOffices();
                setUserSiteOfficeAccess(currentMember.siteOfficeId || null);
          } else {
            console.log('⚠️ Session invalidated:', currentMember?.isBlocked ? 'User is blocked' : 'User inactive or not found');
            await supabase.auth.signOut();
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userSiteOfficeId');
            if (currentMember?.isBlocked) {
              alert(`Akun Anda telah diblokir${currentMember.blockReason ? `: ${currentMember.blockReason}` : ''}. Hubungi administrator.`);
            }
          }
        } else {
          // No Supabase session - also try localStorage as fallback
          const storedMember = localStorage.getItem('currentUser');
          if (storedMember) {
            // localStorage exists but no Supabase session - clear stale data
            console.log('⚠️ No Supabase session but localStorage found - clearing stale data');
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userSiteOfficeId');
          }
        }
      } catch (error) {
        console.error('Error recovering session:', error);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userSiteOfficeId');
      } finally {
        setIsLoading(false);
      }
    };

    recoverSession();

    // Listen for auth state changes - NEVER await inside this callback to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔑 Auth state change:', event);
      
      if (event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          // Defer async work to avoid deadlock
          setTimeout(async () => {
            try {
              const currentMember = await TeamService.findTeamMemberByEmail(session.user.email!.toLowerCase());
              if (currentMember && currentMember.status === 'active' && !currentMember.isBlocked) {
                setTeamMember(currentMember);
                localStorage.setItem('currentUser', JSON.stringify(currentMember));
                localStorage.setItem('userRole', currentMember.role || 'viewer');
                localStorage.setItem('userSiteOfficeId', currentMember.siteOfficeId === null || currentMember.siteOfficeId === undefined ? 'null' : currentMember.siteOfficeId);
                window.dispatchEvent(new Event('userRoleChanged'));
              }
            } catch (error) {
              console.error('Error recovering member on auth change:', error);
            }
          }, 0);
        }
      } else if (event === 'SIGNED_IN') {
        // Skip if signIn() already handled this - avoid race condition
        if (signInHandledRef.current) {
          signInHandledRef.current = false;
          return;
        }
        // Only fetch if teamMember not already set (e.g. page reload recovery)
        if (!teamMember && session?.user) {
          setTimeout(async () => {
            try {
              const currentMember = await TeamService.findTeamMemberByEmail(session.user.email!.toLowerCase());
              if (currentMember && currentMember.status === 'active' && !currentMember.isBlocked) {
                setTeamMember(currentMember);
                localStorage.setItem('currentUser', JSON.stringify(currentMember));
                localStorage.setItem('userRole', currentMember.role || 'viewer');
                localStorage.setItem('userSiteOfficeId', currentMember.siteOfficeId === null || currentMember.siteOfficeId === undefined ? 'null' : currentMember.siteOfficeId);
                window.dispatchEvent(new Event('userRoleChanged'));
              }
            } catch (error) {
              console.error('Error recovering member on auth change:', error);
            }
          }, 0);
        }
      } else if (event === 'SIGNED_OUT') {
        setTeamMember(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userSiteOfficeId');
        window.dispatchEvent(new Event('userRoleChanged'));
      }
    });
    
    // Periodically check if user is still valid (every 5 minutes - reduced to avoid DB pressure)
    const sessionCheckInterval = setInterval(async () => {
      const storedMember = localStorage.getItem('currentUser');
      if (storedMember) {
        try {
          const member = JSON.parse(storedMember);
          const currentMember = await TeamService.findTeamMemberByEmail(member.email);
          
          if (!currentMember || currentMember.status !== 'active' || currentMember.isBlocked) {
            console.log('⚠️ Session check failed - forcing logout');
            await supabase.auth.signOut();
            setTeamMember(null);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userSiteOfficeId');
            
            if (currentMember?.isBlocked) {
              alert(`Akun Anda telah diblokir${currentMember.blockReason ? `: ${currentMember.blockReason}` : ''}. Hubungi administrator.`);
            }
            
            window.location.href = '/login';
          }
        } catch (error) {
          console.error('Session check error:', error);
        }
      }
    }, 300000); // Check every 5 minutes

    return () => {
      clearInterval(sessionCheckInterval);
      subscription.unsubscribe();
    };
  }, []);

  const checkMembershipStatus = async (email: string): Promise<boolean> => {
    try {
      const member = await TeamService.findTeamMemberByEmail(email);
      return member !== null;
    } catch (error) {
      console.error('Error checking membership status:', error);
      return false;
    }
  };

  // DEPRECATED: Use signIn instead - this function is kept for backward compatibility
  // but now uses Supabase Auth exclusively for security
  const login = async (email: string, password: string): Promise<boolean> => {
    const result = await signIn(email, password);
    return result.success;
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string; needsEmailConfirmation?: boolean }> => {
    try {
      setIsLoading(true);
      
      // Mark that signIn is handling this to prevent onAuthStateChange race condition
      signInHandledRef.current = true;
      
      // Try Supabase Auth - this is the ONLY way to authenticate
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password,
      });

      if (authError || !authData.user) {
        signInHandledRef.current = false;
        const errAny = authError as any;
        const code = (errAny?.code || errAny?.error_code || '').toString();
        const msg = (authError?.message || '').toLowerCase();

        // Check for email not confirmed
        if (code === 'email_not_confirmed' || msg.includes('email not confirmed')) {
          return {
            success: false,
            needsEmailConfirmation: true,
            error: 'Email belum dikonfirmasi. Silakan cek inbox/spam, lalu klik link verifikasi.',
          };
        }

        // Check for invalid credentials
        if (msg.includes('invalid login credentials') || msg.includes('invalid password')) {
          return {
            success: false,
            error: 'Email atau password salah. Silakan coba lagi.',
          };
        }

        return {
          success: false,
          error: 'Terjadi kesalahan saat login. Silakan coba lagi.',
        };
      }

      // Supabase Auth successful - fetch member data from database
      const member = await TeamService.findTeamMemberByEmail(email.toLowerCase());
      
      if (!member) {
        // User exists in Supabase Auth but not in team_members - sign out
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Akun tidak ditemukan dalam sistem. Hubungi administrator.',
        };
      }

      // Check member status
      if (member.approvalStatus !== 'approved') {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Akun Anda belum disetujui administrator. Silakan tunggu persetujuan.',
        };
      }

      if (member.status !== 'active') {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Akun Anda tidak aktif. Hubungi administrator.',
        };
      }

      // Check if member is blocked
      if (member.isBlocked) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: `Akun Anda diblokir${member.blockReason ? `: ${member.blockReason}` : ''}. Hubungi administrator untuk membuka blokir.`,
        };
      }

      // Set authenticated user in app state (without storing password)
      const safeMember = { ...member, password: undefined };
      setTeamMember(safeMember as TeamMember);
      localStorage.setItem('currentUser', JSON.stringify(safeMember));
      localStorage.setItem('userRole', member.role || 'viewer');
      localStorage.setItem('userSiteOfficeId', member.siteOfficeId === null || member.siteOfficeId === undefined ? 'null' : member.siteOfficeId);
      window.dispatchEvent(new Event('userRoleChanged'));

      // Refresh site offices now that user is authenticated
      await refreshSiteOffices();
      setUserSiteOfficeAccess(member.siteOfficeId || null);

      return { success: true };

    } catch (error) {
      return { 
        success: false, 
        error: 'Terjadi kesalahan saat login. Silakan coba lagi.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = useCallback(async (): Promise<void> => {
    try {
      // Disconnect presence channel BEFORE signing out
      disconnectPresence();
      await supabase.auth.signOut();
      setTeamMember(null);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('userRole');
      localStorage.removeItem('approvedMember');
      localStorage.removeItem('userSiteOfficeId');
      window.dispatchEvent(new Event('userRoleChanged'));
      localStorage.removeItem('selectedSiteOffice');
      localStorage.removeItem('selectedSiteOfficeId');
      localStorage.removeItem('selectedSiteOfficeUuid');
      // Reset SiteOfficeContext state properly
      clearSiteOffice();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  // Idle timeout DISABLED - session persists until member signs out manually

  // Check if user can access all site offices (siteOfficeId is null)
  const canAccessAllSiteOffices = teamMember?.siteOfficeId === null || teamMember?.siteOfficeId === undefined;

  // Check if user can access a specific site office
  const canAccessSiteOffice = (siteOfficeId: string): boolean => {
    if (!teamMember) return false;
    // If member has null siteOfficeId, they can access all site offices
    if (teamMember.siteOfficeId === null || teamMember.siteOfficeId === undefined) return true;
    // Otherwise, check if the site office matches
    return teamMember.siteOfficeId === siteOfficeId;
  };

  const value: AuthContextType = {
    teamMember,
    currentRole: (teamMember?.role || 'viewer') as UserRole,
    isAuthenticated: !!teamMember && teamMember.status === 'active',
    isLoading,
    siteOfficeId: teamMember?.siteOfficeId || null,
    canAccessAllSiteOffices,
    canAccessSiteOffice,
    signIn,
    signOut,
    checkMembershipStatus,
    login,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 